import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { calendarService } from '../services/calendarService';
import { StudentEvent, CreateEventInput, UpdateEventInput } from '../types/calendar';

export const calendarKeys = {
  all: ['calendar'] as const,
  events: (start?: string, end?: string) => [...calendarKeys.all, 'events', start, end] as const,
  upcoming: (limit?: number) => [...calendarKeys.all, 'upcoming', limit] as const,
};

export function useCalendarEvents(start?: string, end?: string) {
  return useQuery<StudentEvent[], Error>({
    queryKey: calendarKeys.events(start, end),
    queryFn: () => calendarService.getEvents(start, end),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useUpcomingEvents(limit = 7) {
  return useQuery<StudentEvent[], Error>({
    queryKey: calendarKeys.upcoming(limit),
    queryFn: () => calendarService.getUpcomingEvents(limit),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation<StudentEvent, Error, CreateEventInput>({
    mutationFn: calendarService.createEvent,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: calendarKeys.all });
    },
  });
}

export function useUpdateEvent() {
  const qc = useQueryClient();
  return useMutation<StudentEvent, Error, { id: string; input: UpdateEventInput }>({
    mutationFn: ({ id, input }) => calendarService.updateEvent(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: calendarKeys.all });
    },
  });
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: calendarService.deleteEvent,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: calendarKeys.all });
    },
  });
}
